import { createServerFn } from "@tanstack/react-start"
import { z } from "zod"
import { requireInventoryUserId } from "@/inventory/lib/auth.server"
import {
  adjustSupplyStock,
  getSuppliesDashboard,
  listSupplyMovements,
  receiveSupplyStock,
  useSupplyStock,
} from "../lib/movements.server"

const movementFiltersSchema = z
  .object({
    itemId: z.number().optional(),
    type: z.enum(["receive", "use", "adjust"]).optional(),
    vendorId: z.number().optional(),
    createdAfter: z.number().optional(),
    createdBefore: z.number().optional(),
    page: z.number().int().positive().optional(),
    pageSize: z.number().int().positive().optional(),
  })
  .optional()

export const listSupplyMovementsFn = createServerFn({ method: "GET" })
  .validator((data?: unknown) => movementFiltersSchema.parse(data ?? {}))
  .handler(async ({ data }) => {
    await requireInventoryUserId()
    return listSupplyMovements(data ?? {})
  })

export const getSuppliesDashboardFn = createServerFn({ method: "GET" }).handler(
  async () => {
    await requireInventoryUserId()
    return getSuppliesDashboard()
  },
)

export const receiveSupplyStockFn = createServerFn({ method: "POST" })
  .validator((data: unknown) =>
    z
      .object({
        itemId: z.number(),
        quantity: z.number().int().positive(),
        vendorId: z.number().nullable().optional(),
        invoiceNumber: z.string().nullable().optional(),
        unitCostCents: z.number().int().nonnegative().nullable().optional(),
        note: z.string().nullable().optional(),
      })
      .parse(data),
  )
  .handler(async ({ data }) => {
    const userId = await requireInventoryUserId()
    return receiveSupplyStock(userId, data)
  })

export const useSupplyStockFn = createServerFn({ method: "POST" })
  .validator((data: unknown) =>
    z
      .object({
        itemId: z.number(),
        quantity: z.number().int().positive(),
        note: z.string().nullable().optional(),
      })
      .parse(data),
  )
  .handler(async ({ data }) => {
    const userId = await requireInventoryUserId()
    return useSupplyStock(userId, data)
  })

export const adjustSupplyStockFn = createServerFn({ method: "POST" })
  .validator((data: unknown) =>
    z
      .object({
        itemId: z.number(),
        quantityDelta: z
          .number()
          .int()
          .refine((n) => n !== 0, {
            message: "quantityDelta must be non-zero",
          }),
        note: z.string(),
      })
      .parse(data),
  )
  .handler(async ({ data }) => {
    const userId = await requireInventoryUserId()
    return adjustSupplyStock(userId, data)
  })
